import { useSettingsContext } from "@/components/Settings/hooks/useSettingsContext";
import axios from "axios";
import dayjs from "dayjs";
import { REGISTERED_LANGUAGES, RegisteredLangNames } from "../locales/names";
import { useTranslation } from "react-i18next";
import { default as languageService } from "../services/language";

const useLangs = () => {
  const Settings = useSettingsContext();
  const { i18n } = useTranslation();

  const availableLangs: RegisteredLangNames[] = REGISTERED_LANGUAGES.filter(
    (lang) => Settings.language.partner.find(
      (enabledLang) => enabledLang === lang.value)
  ).map(lang => lang.value);

  const getLangData = (lang: RegisteredLangNames) => (
    REGISTERED_LANGUAGES.filter(regLang => regLang.value == lang)?.[0]
  );

  const availableLangsCodes = availableLangs.map(lang => getLangData(lang).langCode);

  const availableLangsData = REGISTERED_LANGUAGES.filter(
    lang => availableLangs.includes(lang.value));

  const defaultLang = availableLangs.find(
    lang => lang == Settings.language.default
  ) ?? availableLangs.slice(-1)[0];

  const handleResource = async (lang: RegisteredLangNames) => {
    const resourceHasBeenLoaded = i18n.hasResourceBundle(lang, 'common');

    if (!resourceHasBeenLoaded) {
      await languageService.fetchTranslations(lang)
      .then(res => {
        i18n.addResourceBundle(lang, 'common', res);
      })
      .catch(err => {
        console.error(err);
      })
    }
  }

  const getBCP47Lang = (POSIXLang: string) => {
    return POSIXLang.replaceAll('_', '-');
  }

  const changeLang = async (newLang: string) => {
    if (!checkLangAvailable(newLang)) {
      setLangDefault();
      console.warn("Language", newLang, "not available. Could not be set.");
      return;
    }

    await handleResource(newLang as RegisteredLangNames);

    i18n.changeLanguage(newLang);
    dayjs.locale(getLangData(newLang as RegisteredLangNames).langCode);
    axios.defaults.headers.common["Accept-Language"] = newLang;
  };

  const currentLang: RegisteredLangNames = availableLangs.find(
    lang => lang === i18n.language) ?? defaultLang;

  const currentLangData = getLangData(currentLang);

  const getLangByCode = (lang: string) => {
    return availableLangs.find(
      (avLang) => avLang == lang.replace('_', '-'));
  }

  const getAllLangsPath = (path: string): string[] => {
    return availableLangs.map((value) => i18n.getFixedT(value)(path));
  }

  const checkLangAvailable = (lang: RegisteredLangNames | (string & {})) => {
    return !!availableLangs.find(avLang => avLang == lang)
  }

  const setLangDefault = () => {
    changeLang(defaultLang);
    return defaultLang;
  }

  return {
    availableLangs,
    availableLangsCodes,
    availableLangsData,
    changeLang,
    currentLangData,
    checkLangAvailable,
    currentLang,
    getAllLangsPath,
    getBCP47Lang,
    getLangByCode,
    getLangData,
    setLangDefault
  }
}

export default useLangs;
